import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, MailX } from "lucide-react";
import { api } from "@/lib/api";

export default function Unsubscribe() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [state, setState] = useState("loading");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) {
      setState("invalid");
      return;
    }
    api
      .post("/unsubscribe", { token })
      .then((res) => {
        setEmail(res.data.email);
        setState("done");
      })
      .catch(() => setState("invalid"));
  }, [token]);

  const resubscribe = async () => {
    setBusy(true);
    try {
      await api.post("/resubscribe", { token });
      setState("resubscribed");
    } catch {
      setState("invalid");
    } finally {
      setBusy(false);
    }
  };

  return (
    <main data-testid="unsubscribe-page" className="min-h-screen bg-[#0D0B0E] text-slate-100 flex items-center justify-center px-6 py-20">
      <div className="absolute left-1/2 top-1/3 -translate-x-1/2 w-[30rem] h-[30rem] rounded-full bg-[#E890A7]/8 blur-[120px] pointer-events-none" />
      <div className="relative w-full max-w-lg rounded-[2rem] border border-[#E890A7]/20 bg-gradient-to-br from-[#1E1A23] to-[#151218] p-8 sm:p-12">
        <p className="font-mono text-[10px] tracking-[0.35em] uppercase text-[#E890A7] mb-4 flex items-center gap-2">
          <MailX size={13} /> Market Report preferences
        </p>

        {state === "loading" && (
          <div className="flex items-center gap-3 text-slate-400" data-testid="unsubscribe-loading">
            <Loader2 className="animate-spin" size={18} /> Updating your preferences…
          </div>
        )}

        {state === "done" && (
          <div data-testid="unsubscribe-success">
            <h1 className="font-display font-light text-3xl sm:text-4xl leading-tight">
              You&apos;ve been <em className="italic text-[#E890A7]">unsubscribed</em>
            </h1>
            <p className="mt-4 text-slate-400 font-light">
              <span className="text-slate-200" data-testid="unsubscribe-email">{email}</span> will no longer receive
              the Lagoon City market report. Sorry to see you go.
            </p>
            <button
              data-testid="resubscribe-btn"
              onClick={resubscribe}
              disabled={busy}
              className="mt-8 rounded-full bg-[#E890A7] text-[#0D0B0E] font-semibold px-7 py-3.5 text-sm flex items-center gap-2 hover:bg-[#F2A7BA] disabled:opacity-60 transition-colors"
            >
              {busy && <Loader2 size={15} className="animate-spin" />}
              Unsubscribed by mistake? Re-subscribe
            </button>
          </div>
        )}

        {state === "resubscribed" && (
          <div data-testid="resubscribe-success">
            <h1 className="font-display font-light text-3xl sm:text-4xl leading-tight flex items-center gap-3">
              <CheckCircle2 className="text-[#E890A7]" size={30} /> Welcome back
            </h1>
            <p className="mt-4 text-slate-400 font-light">
              <span className="text-slate-200">{email}</span> is back on the list. The next edition will land in your inbox.
            </p>
          </div>
        )}

        {state === "invalid" && (
          <div data-testid="unsubscribe-invalid">
            <h1 className="font-display font-light text-3xl sm:text-4xl leading-tight">This link isn&apos;t valid</h1>
            <p className="mt-4 text-slate-400 font-light">
              The unsubscribe link is missing or has expired. Please use the link at the bottom of any market report
              email, or email{" "}
              <a href="mailto:joselle@lagooncityrealestate.ca" className="text-[#E890A7]">joselle@lagooncityrealestate.ca</a>.
            </p>
          </div>
        )}

        <Link
          data-testid="unsubscribe-home-link"
          to="/#newsletter"
          className="mt-10 inline-block text-sm text-slate-500 hover:text-[#F2A7BA] transition-colors"
        >
          ← Back to lagooncityrealestate.ca
        </Link>
      </div>
    </main>
  );
}
